import { useState, useEffect, useCallback } from 'react';
import { getToken, getTokenContent } from '../../../utils/token';
import { fetchSimulatorTemplates } from '../services/simulatorTemplates.service';
import type { SimulatorTemplate } from '../types';

const SIM_TABLE_TEMPLATE_STORAGE_KEY = 'simTableTemplate';

/**
 * Custom hook for fetching and managing simulator email templates
 * Persists selected template UUID to localStorage
 * @returns Object containing templates array, selectedTemplate, selectTemplate function, loading, error, and refetch
 */
export const useSimulatorTemplates = () => {
  const [templates, setTemplates] = useState<SimulatorTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<SimulatorTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadTemplates = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    const tokenContent = getTokenContent();
    const adminUserUuid = tokenContent?.payload?.user?.uuid;
    if (!adminUserUuid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const fetchedTemplates = await fetchSimulatorTemplates(token, adminUserUuid);
      setTemplates(fetchedTemplates);

      const savedTemplateUuid = localStorage.getItem(SIM_TABLE_TEMPLATE_STORAGE_KEY);
      const savedTemplate = savedTemplateUuid ? fetchedTemplates.find((t) => t.uuid === savedTemplateUuid) : null;
      const templateToSelect = savedTemplate ?? fetchedTemplates[0] ?? null;

      setSelectedTemplate(templateToSelect);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch templates'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const selectTemplate = useCallback((template: SimulatorTemplate | null) => {
    setSelectedTemplate(template);
    if (template) {
      localStorage.setItem(SIM_TABLE_TEMPLATE_STORAGE_KEY, template.uuid);
    } else {
      localStorage.removeItem(SIM_TABLE_TEMPLATE_STORAGE_KEY);
    }
  }, []);

  return {
    templates,
    selectedTemplate,
    loading,
    error,
    selectTemplate,
    refetch: loadTemplates,
  };
};
